import { DateRange } from 'react-day-picker';

import { FormikTouched } from 'formik';
import { FormikErrors } from 'formik';

import DatePicker from '../../../../components/DatePicker/DatePicker';
import { DatePickerMode } from '../../../../components/DatePicker/DatePicker.types';
import Input from '../../../../components/Input/Input';
import RadioButtonGroup from '../../../../components/RadioButton/RadioButtonGroup';
import { CouponFormValues, EExpirationMode } from '../../types';
import { applyExpirationModeChange, deriveExpirationMode } from '../../utils';

interface GeneralSettingsProps {
  couponFormValues: CouponFormValues;
  edit: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  errors: FormikErrors<CouponFormValues>;
  touched: FormikTouched<CouponFormValues>;
  setFieldTouched: (field: string, isTouched?: boolean | undefined) => void;
  setFieldValue: (field: string, value: any) => void;
  neverExpires: boolean;
  dup: boolean;
  setNeverExpires: (value: boolean) => void;
}

export default function CouponsGeneralSettings({
  couponFormValues,
  edit,
  handleChange,
  handleBlur,
  errors,
  touched,
  setFieldTouched,
  setFieldValue,
  neverExpires,
  dup,
  setNeverExpires
}: GeneralSettingsProps) {
  const isTemporary = couponFormValues.expirationMode === EExpirationMode.TEMPORARY;

  const commonDatePickerProps = {
    label: 'Valid From',
    labelClassName: 'text-sm text-base-foreground',
    labelSpacing: 'mb-2',
    isRequired: true,
    shouldExcludePast: true,
    displayInUTC: true
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex gap-[18px]">
        <Input
          label="Name"
          name="name"
          dataTestId="couponNameInput"
          value={couponFormValues.name}
          disabled={edit}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired={true}
          state={Boolean(errors.name) && (touched.name || dup) ? 'error' : 'default'}
          description={touched.name || dup ? errors.name?.toString() : ''}
          placeholder="Title"
          className="w-[296px]"
        />
        <Input
          label="Discount %"
          name="discountPercentage"
          dataTestId="couponDiscountPercentageInput"
          value={couponFormValues.discountPercentage}
          disabled={edit}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired={true}
          type="number"
          state={Boolean(errors.discountPercentage) && touched.discountPercentage ? 'error' : 'default'}
          description={touched.discountPercentage ? errors.discountPercentage?.toString() : ''}
          placeholder="% Number"
          className="w-[296px]"
        />
      </div>
      <div className="flex flex-col items-start gap-[30px]">
        <RadioButtonGroup
          dataTestId="couponExpirationMode"
          className="[&_button]:border [&_button]:border-base-border [&_button]:h-4 [&_button]:w-4 [&_svg]:!h-2 [&_svg]:!w-2"
          value={
            couponFormValues.expirationMode ||
            deriveExpirationMode(
              couponFormValues.startsAt,
              couponFormValues.expiredBy
            )
          }
          onValueChange={(value) => {
            setFieldValue('expirationMode', value);
            applyExpirationModeChange(
              value as EExpirationMode,
              setFieldValue,
              setFieldTouched,
              setNeverExpires,
              edit
            );
          }}
          options={[
            { value: EExpirationMode.PERMANENT, label: 'Permanent' },
            { value: EExpirationMode.TEMPORARY, label: 'Temporary' }
          ]}
        />
        <div className="w-full">
          {isTemporary ? (
            <>
              <DatePicker
                {...commonDatePickerProps}
                dataTestId="couponDateRange"
                className="w-fit min-w-[296px]"
                mode={DatePickerMode.RANGE}
                numberOfMonths={2}
                value={
                  couponFormValues.startsAt || couponFormValues.expiredBy
                    ? {
                        from: couponFormValues.startsAt ? new Date(couponFormValues.startsAt) : undefined,
                        to: couponFormValues.expiredBy ? new Date(couponFormValues.expiredBy) : undefined
                      }
                    : undefined
                }
                onChange={(value) => {
                  const range = value as DateRange | undefined;
                  setFieldTouched('startsAt', true);
                  setFieldTouched('expiredBy', true);
                  setFieldValue('startsAt', range?.from?.toISOString() || null);
                  setFieldValue('expiredBy', range?.to?.toISOString() || null);
                }}
              />
              {(touched.startsAt || touched.expiredBy) && (errors.startsAt || errors.expiredBy) && (
                <p className="text-sm text-red-500 mt-1">
                  {(errors.startsAt || errors.expiredBy)?.toString()}
                </p>
              )}
            </>
          ) : (
            <>
              <DatePicker
                {...commonDatePickerProps}
                dataTestId="couponStartDate"
                className="w-[296px]"
                mode={DatePickerMode.SINGLE}
                value={couponFormValues.startsAt ?? undefined}
                onChange={(value) => {
                  setFieldTouched('startsAt', true);
                  setFieldValue('startsAt', value || null);
                }}
              />
              {touched.startsAt && errors.startsAt && (
                <p className="text-sm text-red-500 mt-1">{errors.startsAt.toString()}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
